import { SpecialZoomLevel, Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import {
  ToolbarSlot,
  TransformToolbarSlot,
  toolbarPlugin,
} from "@react-pdf-viewer/toolbar";
import "@react-pdf-viewer/toolbar/lib/styles/index.css";
import React, { ReactNode, useEffect, useState } from "react";
import styled from "styled-components";

import Overlay from "../Overlay";

import { PDFOverlay } from "./PDFOverlay";

interface ToolbarContainerProps {
  noBorder?: boolean;
}

const ToolbarContainer = styled.div<ToolbarContainerProps>`
  align-items: center;
  background-color: #f8f8f8;
  border: ${(props) => (props.noBorder ? "none" : "solid 1px #ddd")};
  border-bottom: none;
  display: flex;
  padding: 0.8rem 0.6rem;

  & .rpv-toolbar__label::before {
    content: " / ";
  }
  & svg {
    display: block;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ViewerContainer = styled.div<{
  isLoaded?: boolean;
}>`
  --rpv-core__page-layer-box-shadow: none;
  flex: 1;
  overflow: hidden;
  visibility: ${(props) => (props.isLoaded ? "visible" : "hidden")};

  .rpv-core__doc-loading {
    visibility: hidden;
  }
  .rpv-core__viewer {
    border: none;
  }
  .rpv-core__inner-pages {
    background-color: #888;
    padding: 1rem 0 0;
    box-sizing: border-box;
    box-shadow: inset 0 0 1rem 0.2rem rgba(112, 91, 91, 0.9);
  }
  .rpv-core__inner-page {
    background-color: transparent;
  }
`;

interface Props {
  url?: string;
  overlay?: ReactNode;
  showPrint?: boolean;
  noBorder?: boolean;
}

const PDFDocument: React.VFC<Props> & {
  Overlay: typeof PDFOverlay;
} = (props) => {
  const toolbarPluginInstance = toolbarPlugin();
  const { renderDefaultToolbar, Toolbar } = toolbarPluginInstance;
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(false);
  }, [props.url]);

  // Turn off a bunch of the default functionality that comes with the toolbar
  const transform: TransformToolbarSlot = (slot: ToolbarSlot) => {
    const result = {
      ...slot,
      // These slots will be empty
      // Download: () => <></>,
      SwitchTheme: () => <></>,
      Open: () => <></>,
      ShowProperties: () => <></>,
      SwitchScrollMode: () => <></>,
      SwitchSelectionMode: () => <></>,
      SwitchViewMode: () => <></>,
    };
    if (props.showPrint !== true) {
      result.Print = () => <></>;
    }
    return result;
  };

  return (
    <Overlay
      overlayContent={props.overlay}
      overlayStyle={{ backdropFilter: "blur(4px)" }}
      showOverlay={!!props.overlay}
    >
      {props.url && (
        <>
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.1.81/build/pdf.worker.min.js" />
          <Container className="rpv-core__viewer">
            <ToolbarContainer noBorder={props.noBorder}>
              <Toolbar>{renderDefaultToolbar(transform)}</Toolbar>
            </ToolbarContainer>
            <ViewerContainer isLoaded={isLoaded}>
              <Viewer
                onZoom={() => {
                  setIsLoaded(true);
                }}
                pageLayout={{
                  tranformSize: ({ size }) => ({
                    height: size.height + 10,
                    width: size.width,
                  }),
                }}
                fileUrl={props.url || ""}
                plugins={[toolbarPluginInstance]}
                defaultScale={SpecialZoomLevel.PageWidth}
                httpHeaders={{
                  // use Etag for caching
                  "cache-control": "no-cache",
                }}
              />
            </ViewerContainer>
          </Container>
        </>
      )}
    </Overlay>
  );
};

PDFDocument.Overlay = PDFOverlay;

export default PDFDocument;
